import { Injectable } from '@angular/core';

// NOTE: Adjust these imports to the actual package API if needed.
// This wrapper keeps the rest of the app decoupled from the library specifics.
// If the package name differs, update it here.
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import ReceiptPrinter from '@point-of-sale/webserial-receipt-printer';
// @ts-ignore
import ReceiptPrinterEncoder from '@point-of-sale/receipt-printer-encoder';

export interface PrinterOpenOptions {
  baudRate?: number;
}

export const printerModels = {
  // ---------------------------
  // EPSON (TM series)
  // ---------------------------
  '1208:3616': 'Epson TM-T20',
  '1208:3570': 'Epson TM-T20II',
  '1208:3584': 'Epson TM-T20III',
  '1208:4100': 'Epson TM-T88III',
  '1208:4160': 'Epson TM-T88IV',
  '1208:4161': 'Epson TM-T88IV',
  '1208:4176': 'Epson TM-T88V',
  '1208:4177': 'Epson TM-T88V',
  '1208:4180': 'Epson TM-T88VI',
  '1208:219': 'Epson TM-m30',
  '1208:220': 'Epson TM-m30II',
  '1208:221': 'Epson TM-m30III',
  '1208:4101': 'Epson TM-U220',
  '1208:4170': 'Epson TM-L90',
  '1208:4162': 'Epson TM-T70',
  '1208:4163': 'Epson TM-T70II',

  // ---------------------------
  // STAR MICRONICS
  // ---------------------------
  '1046:20497': 'Star TSP100 (TSP143)',
  '1046:20498': 'Star TSP113',
  '1046:51': 'Star TSP650II',
  '1046:50': 'Star TSP654II',
  '1046:20499': 'Star mPOP',
  '1046:4096': 'Star SM-S230i',
  '1046:4097': 'Star SM-T300i',

  // ---------------------------
  // BIXOLON
  // ---------------------------
  '1504:325': 'Bixolon SRP-350',
  '1504:326': 'Bixolon SRP-350II',
  '1504:327': 'Bixolon SRP-350III',
  '1504:4033': 'Bixolon SRP-330',
  '1504:4034': 'Bixolon SRP-330II',
  '1504:4035': 'Bixolon SRP-330III',
  '1504:331': 'Bixolon SPP-R200III',
  '1504:332': 'Bixolon SPP-R210',

  // ---------------------------
  // RONGTA (RPP / RP series)
  // ---------------------------
  '26728:2560': 'Rongta RP80',
  '26728:2561': 'Rongta RP58',
  '26728:2562': 'Rongta RP328',
  '26728:2563': 'Rongta RP850',
  '26728:5000': 'Rongta RPP200',
  '26728:5001': 'Rongta RPP300',

  // ---------------------------
  // XPRINTER (XP series)
  // ---------------------------
  '6790:30084': 'Xprinter XP-58',
  '6790:30085': 'Xprinter XP-58II',
  '6790:30086': 'Xprinter XP-80',
  '6790:30087': 'Xprinter XP-Q80',
  '6790:30088': 'Xprinter XP-T58',
  '6790:30089': 'Xprinter XP-T80',
  '6790:30090': 'Xprinter XP-N160',

  // ---------------------------
  // POS80 / AliExpress clones
  // ---------------------------
  '1155:22304': 'POS-80 (Generic)',
  '1155:22305': 'POS-58 (Generic)',
  '1155:22306': 'POS-80C (Generic)',
  '1155:22307': 'POS-80A (Generic)',
  '1155:22311': 'Zjiang ZJ-5890',
  '1155:22312': 'Zjiang ZJ-80',
  '1155:22313': 'Generic 58mm Bluetooth/USB Printer',

  // ---------------------------
  // BEMATECH
  // ---------------------------
  '3244:1001': 'Bematech MP-4200',
  '3244:1002': 'Bematech MP-2500',
  '3244:1003': 'Bematech MP-4000 TH',
  '3244:1004': 'Bematech MP-20 Mi',

  // ---------------------------
  // SUNMI (USB but uses serial bridge)
  // ---------------------------
  '6353:12304': 'Sunmi V2 / V2 Pro Printer (USB Bridge)',
  '6353:12305': 'Sunmi T2 Printer Module',
  '6353:12306': 'Sunmi T2 Mini Printer Module',

  // ---------------------------
  // CUSTOM ENGINE (Italy)
  // ---------------------------
  '10619:1': 'Custom Q3',
  '10619:2': 'Custom K3',
  '10619:3': 'Custom K80',
  '10619:4': 'Custom TG02',

  // ---------------------------
  // CITIZEN
  // ---------------------------
  '1067:2': 'Citizen CT-S310',
  '1067:3': 'Citizen CT-S310II',
  '1067:4': 'Citizen CT-S601',
  '1067:5': 'Citizen CT-S651',
  '1067:6': 'Citizen CBM1000',

  // ---------------------------
  // DATECS
  // ---------------------------
  '1208:20480': 'Datecs DPP-250',
  '1208:20481': 'Datecs DPP-350',
  '1208:20482': 'Datecs DPP-450',

  // ---------------------------
  // UNKNOWN / UNIDENTIFIED
  // ---------------------------
  // Adds fallback for common clones
  '0:0': 'Unknown Serial Printer'
};

class TableBuilder {
  constructor(
    private encoderData: any,
    private columns: { width: number; align: 'left' | 'right' | 'center' }[]
  ) {}

  row(...cells: (string | number)[]) {
    const line = this.columns.map((col, i) => formatCell(String(cells[i] ?? ''), col.width, col.align)).join('');

    this.encoderData.line(line);
    this.encoderData.newline();
    return this;
  }
}

function formatCell(text: string, width: number, align: 'left' | 'right' | 'center'): string {
  if (text.length > width) return text.substring(0, width);

  const space = width - text.length;

  switch (align) {
    case 'right':
      return ' '.repeat(space) + text;

    case 'center':
      const left = Math.floor(space / 2);
      return ' '.repeat(left) + text + ' '.repeat(space - left);

    default:
      return text + ' '.repeat(space);
  }
}

@Injectable({ providedIn: 'root' })
export class WebSerialPrinterService {
  private _printer: any | null = null;
  private connected = false;
  private _encoder: any;
  private lastUsedDevice: any;
  private encoderData: any;
  private readonly aliasStorageKey = 'printerAliases';
  private readonly registerPrinterKeyPrefix = 'registerPrinter:';

  /** Expose last used device info for UI */
  get lastDevice(): any {
    return this.lastUsedDevice;
  }

  // Lazy initialization getter
  public get printer(): any {
    if (!this._printer) {
      this._printer = new ReceiptPrinter();
      this.setupPrinterEventListeners();
    }
    return this._printer;
  }

  // Lazy initialization getter
  public get encoder(): any {
    if (!this._encoder) {
      this._encoder = new ReceiptPrinterEncoder();
    }
    return this._encoder;
  }

  private setupPrinterEventListeners() {
    if (!this._printer) return;

    this._printer.addEventListener('connected', device => {
      /* Store device for reconnecting */
      this.lastUsedDevice = device;

      localStorage.setItem('lastUsedDevice', JSON.stringify(device));

      this.connected = true;

      if (this.encoderData) {
        try {
          if (this.encoderData instanceof Uint8Array) {
            this._printer.print(this.encoderData);
          } else if (this.encoderData?.encode) {
            const data = this.forceNewLine().encode();
            this._printer.print(data);
          }
          this.encoderData = null;
        } catch (e) {
          console.error('Error printing pending buffer on connect', e);
        }
      }
    });

    // Uncomment if needed:
    // this._printer.addEventListener('disconnected', () => {
    //   this.lastUsedDevice = null;
    //   this.connected = false;
    //   this.encoderData = null;
    //   localStorage.removeItem('lastUsedDevice');
    // });
  }

  public set printer(value: any) {
    this._printer = value;
  }

  public set encoder(value: any) {
    this._encoder = value;
  }

  /** Build a unique key for alias map */
  private getPrinterKey(info: { vendorId?: number | null; productId?: number | null }): string {
    const v = info?.vendorId ?? 0;
    const p = info?.productId ?? 0;
    return `${v}:${p}`;
  }

  /** Read aliases map from localStorage */
  private readAliases(): Record<string, string> {
    try {
      const raw = localStorage.getItem(this.aliasStorageKey);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? (parsed as Record<string, string>) : {};
    } catch {
      return {};
    }
  }

  /** Persist aliases map to localStorage */
  private writeAliases(map: Record<string, string>): void {
    try {
      localStorage.setItem(this.aliasStorageKey, JSON.stringify(map));
    } catch {
      // ignore
    }
  }

  /** Get alias for a printer (if any) */
  getPrinterAlias(info: { vendorId?: number | null; productId?: number | null }): string | null {
    const key = this.getPrinterKey(info);
    const map = this.readAliases();
    return map[key] || null;
  }

  /** Set or clear alias for a printer. Pass empty string/null to clear */
  setPrinterAlias(info: { vendorId?: number | null; productId?: number | null }, alias: string | null | undefined): void {
    const key = this.getPrinterKey(info);
    const map = this.readAliases();
    const trimmed = (alias ?? '').trim();
    if (!trimmed) {
      if (key in map) delete map[key];
    } else {
      map[key] = trimmed;
    }
    this.writeAliases(map);
  }

  identifyPrinter(info?: { vendorId?: number | null; productId?: number | null; productName?: string | null } | null): string {
    if (!info) return 'Unknown';
    const key = this.getPrinterKey(info);
    const alias = this.getPrinterAlias(info);
    const printerModel = printerModels[key];
    const baseName = info.productName || printerModel || `Printer (${key})`;

    if (alias) {
      return alias + (baseName && baseName !== alias ? ` (${baseName})` : '');
    }
    return baseName || 'Unknown ESC/POS Printer';
  }

  constructor() {
    const lastUsedDevice = localStorage.getItem('lastUsedDevice');
    if (lastUsedDevice) {
      try {
        this.lastUsedDevice = JSON.parse(lastUsedDevice);
        console.log(this.identifyPrinter(this.lastUsedDevice));
      } catch (e) {
        console.error('Error parsing last used device', e);
      }
    }
  }

  public setDefaultPrinter(printer: any) {
    localStorage.setItem('lastUsedDevice', JSON.stringify(printer));
    this.lastUsedDevice = printer;
  }

  private getRegisterStorageKey(registerId: number | string): string {
    return `${this.registerPrinterKeyPrefix}${registerId}`;
  }

  public setRegisterDefaultPrinter(
    registerId: number | string,
    printer?: { vendorId: number | null; productId: number | null } | null
  ): void {
    const key = this.getRegisterStorageKey(registerId);
    try {
      if (!printer || (printer.vendorId == null && printer.productId == null)) {
        localStorage.removeItem(key);
      } else {
        localStorage.setItem(key, JSON.stringify(printer));
      }
    } catch {
      // ignore storage errors
    }
  }

  public getRegisterDefaultPrinter(registerId: number | string): { vendorId: number | null; productId: number | null } | null {
    const key = this.getRegisterStorageKey(registerId);
    try {
      const raw = localStorage.getItem(key);
      return raw ? (JSON.parse(raw) as { vendorId: number | null; productId: number | null }) : null;
    } catch {
      return null;
    }
  }

  private forceNewLine() {
    return this.encoderData.newline().newline().newline().newline().newline().newline().newline().cut();
  }

  get isSupported(): boolean {
    return typeof navigator !== 'undefined' && !!(navigator as any).serial;
  }

  table(columns: { width: number; align: 'left' | 'right' | 'center' }[]) {
    if (!this.encoderData) {
      this.encoderData = this.encoder.initialize();
    }
    return new TableBuilder(this.encoderData, columns);
  }

  open() {
    if (!this.isSupported) {
      throw new Error('Web Serial is not supported by this browser.');
    }

    if (!this.lastUsedDevice) {
      try {
        this.printer.connect({ baudRate: 3 }).then(printer => {
          if (printer?.init) {
            printer.init().then(() => {
              console.log('Printer initialized successfully');
            });
          } else {
            console.log('Printer not initialized');
          }
        });
      } catch (error) {}
    } else {
      this.printer.reconnect(this.lastUsedDevice);
      // this.connected = true;
    }
  }

  ensureOpen() {
    if (!this.connected) {
      this.open();
    }
  }

  printLines(lines: string[], opts?: { align?: 'left' | 'center' | 'right'; bold?: boolean }) {
    if (!this.encoderData) {
      this.encoderData = this.encoder.initialize();
    }
    if (!this.printer) throw new Error('Printer not connected');

    if (opts?.align) {
      this.encoderData = this.encoderData.align(opts.align);
    }

    if (opts?.bold) {
      this.encoderData = this.encoderData.bold(true);
    }

    for (const line of lines) {
      this.encoderData = this.encoderData.line(line);
      this.encoderData = this.encoderData.newline();
    }

    if (opts?.bold) {
      this.encoderData = this.encoderData.bold(false);
    }
  }

  feed(lines = 3) {
    if (!this.printer) throw new Error('Printer not connected');
    if (this.encoderData?.feed) {
      this.encoderData = this.encoderData.feed(lines);
    }
  }

  print() {
    if (!this.printer) throw new Error('Printer not connected');

    if (!this.connected) {
      return;
    }
    this.printer.print(this.forceNewLine().encode());
    this.encoderData = null;
  }

  /** Public helper: connect/open printer (prompts user on first time) */
  connect() {
    this.open();
  }

  /** List Serial ports the user already granted access to */
  async getGrantedPorts(): Promise<{ port: any; info: { usbVendorId?: number; usbProductId?: number } }[]> {
    if (!this.isSupported) return [];
    const serial = (navigator as any).serial;
    const ports = await serial.getPorts();
    const results: { port: any; info: { usbVendorId?: number; usbProductId?: number } }[] = [];
    for (const port of ports) {
      const info = typeof port.getInfo === 'function' ? port.getInfo() : {};
      const vendorId = (info as any).usbVendorId ?? (info as any).vendorId;
      const productId = (info as any).usbProductId ?? (info as any).productId;
      if (vendorId != null && productId != null) {
        const key = `${vendorId}:${productId}`;
        if (printerModels[key] && key !== '0:0') {
          results.push({ port, info });
        }
      }
    }
    return results;
  }

  /** Ask the user to select a new Serial device (adds to granted list) */
  async requestPort(filters?: { usbVendorId?: number; usbProductId?: number }[]): Promise<any> {
    if (!this.isSupported) throw new Error('Web Serial is not supported by this browser.');
    const serial = (navigator as any).serial;

    // If no filters provided, default to supported printer models
    let useFilters =
      filters && filters.length
        ? filters
        : Object.keys(printerModels)
            .filter(k => k !== '0:0')
            .map(k => {
              const [v, p] = k.split(':').map(n => Number(n));
              return { usbVendorId: v, usbProductId: p } as { usbVendorId: number; usbProductId: number };
            });

    // Remove any invalid entries
    useFilters = useFilters.filter(f => typeof f.usbVendorId === 'number' && typeof f.usbProductId === 'number');

    const opts: any = useFilters.length ? { filters: useFilters } : {};
    const port = await serial.requestPort(opts);
    return port;
  }

  /** Simple test print */
  async testPrint() {
    if (this.lastUsedDevice) {
      this.printer.reconnect(this.lastUsedDevice);
    }

    this.encoderData = this.encoder.initialize();
    this.encoderData = this.encoderData.align('center');
    this.encoderData = this.encoderData.bold(true).line('Teste impressao').bold(false);
    this.table([
      { width: 12, align: 'left' }, // adjust to your printer width
      { width: 32, align: 'right' }
    ]).row(`Impressora:`, `${this.identifyPrinter(this.lastUsedDevice)}`);
    this.table([
      { width: 80, align: 'center' } // adjust to your printer width
    ]).row(`NosERP`);
    this.encoderData = this.encoderData.newline().line('Obrigado!');

    // Ensure connection and print
    this.ensureOpen();
    this.print();
  }

  /**
   * Print a sequence of canvases (PDF pages rendered) as raster images.
   */
  async printCanvases(
    canvases: HTMLCanvasElement[],
    opts?: {
      align?: 'left' | 'center' | 'right';
      dither?: boolean;
      marginTopPx?: number;
      marginBottomPx?: number;
      cut?: 'full' | 'partial' | false;
    }
  ) {
    if (!this.printer) throw new Error('Printer not connected');

    const { align = 'center', dither = false, marginTopPx = 8, marginBottomPx = 16, cut = 'full' } = opts || {};

    // Start a fresh encoder buffer
    this.encoderData = this.encoder.initialize();
    this.encoderData = this.encoderData.align(align);

    for (let canvas of canvases) {
      let src = canvas;

      // Ensure width is a multiple of 8 (ESC/POS raster requirement)
      if (src.width % 8 !== 0) {
        const fixedWidth = Math.ceil(src.width / 8) * 8;
        const scale = fixedWidth / src.width;
        const newHeight = Math.round(src.height * scale);

        const resized = document.createElement('canvas');
        resized.width = fixedWidth;
        resized.height = newHeight;

        const ctx = resized.getContext('2d');
        if (!ctx) throw new Error('Cannot get canvas context');
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, resized.width, resized.height);
        ctx.drawImage(src, 0, 0, fixedWidth, newHeight);
        src = resized;
      }

      // Optional vertical margins (approximate: each feed ~ one text line)
      if (marginTopPx > 0) {
        const lines = Math.max(0, Math.round(marginTopPx / 8));
        if (lines > 0 && this.encoderData.feed) this.encoderData = this.encoderData.feed(lines);
      }

      // Follow official sample: pass canvas, width, height, and dithering method
      this.encoderData = this.encoderData.image(src, 64, 64, dither ? 'atkinson' : 'threshold');

      if (marginBottomPx > 0) {
        const lines = Math.max(0, Math.round(marginBottomPx / 8));
        if (lines > 0 && this.encoderData.feed) this.encoderData = this.encoderData.feed(lines);
      }
    }

    if (cut) {
      this.encoderData = this.encoderData.cut(cut);
    }

    const data = this.encoderData.encode();

    this.open();

    this.printer.print(data);
  }
}
